import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoaderCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import authService from "@/services/auth.service";
import Alert from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth.context";

import { Link, useLocation} from "react-router-dom";
import qs from "qs";
import { Checkbox } from "@/components/ui/checkbox";

import { useId } from "react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function Login() {
  const { t } = useTranslation();
  const location = useLocation();

  const formSchema = yup.object().shape({
    email: yup
      .string()
      .email()
      .required(t("validation.required"))
      .label(t("pages.login.email")),
    password: yup.string().required(t("validation.required")).label(t("pages.login.password")),
  });

  const form = useForm({
    resolver: yupResolver(formSchema),
    values: {
      email: "",
      password: "",
    },
  });

  const redirect = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.redirect;

  const defaultEmail = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.email;

  useEffect(() => {
    if (defaultEmail) {
      form.setValue("email", defaultEmail);
    }
  }, [defaultEmail]);

  const { setCurrentUser } = useAuth();

  // const navigate = useNavigate();
  const [error, setError] = useState("");

  const submit = async (values: any) => {
    setError("");
    try {
      setError("");
      const res = await authService.signIn({
        email: values.email,
        password: values.password,
      });
      setCurrentUser(res);
      // navigate(redirect || "/dashboard");
      window.location.href=redirect || "/dashboard";
    } catch (e) {
      setError(e?.response?.data?.message || t("messages.errorOccurred"));
      const errors = e?.response?.data?.meta?.errors || {};
      Object.keys(errors)?.forEach((field: any) => {
        form.setError(field, {
          message: errors[field],
        });
      });
    }
  };

  const id = useId();

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Language Switcher in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>
      <div className="bg-gradient-to-b h-full flex-1 from-blue-50 py-36 to-blue-100 flex items-center justify-center p-4">
        <motion.div
          layout
          className={`h-auto`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            type: "tween",
            duration: 0.2,
            ease: "easeOut",
          }}
        >
          {" "}
          <Card className="w-[400px] transition-all max-w-md">
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg text-center">
                {t("pages.login.title")}
              </CardTitle>
              <CardDescription className="text-center !text-sm">
                {t("pages.login.subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pb-3">
              <div>
                {error && <Alert variant="danger">{error}</Alert>}

                <form onSubmit={form.handleSubmit(submit)}>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="phone">{t("pages.login.email")}</Label>
                      <Input
                        placeholder={t("pages.login.emailPlaceholder")}
                        value={form.watch("email")}
                        onChange={(e) => form.setValue("email", e.target.value)}
                        error={form.formState?.errors["email"]?.message}
                        name="email"
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex !mb-2 items-center justify-between">
                        <Label htmlFor="phone">{t("pages.login.password")}</Label>
                        <Link
                          className="text-sm underline"
                          to="/forgot-password"
                        >
                          {t("pages.login.forgotPassword")}
                        </Link>
                      </div>
                      <Input
                        placeholder={t("pages.login.passwordPlaceholder")}
                        value={form.watch("password")}
                        onChange={(e) =>
                          form.setValue("password", e.target.value)
                        }
                        type="password"
                        error={form.formState?.errors["password"]?.message}
                        name="password"
                      />
                    </div>
                  </div>
                  <div className="flex pt-4 pb-1 items-center gap-2">
                    <Checkbox id={id} />
                    <Label htmlFor={id}>
                      {t("pages.login.agreeTerms")}{" "}
                      <a
                        className="underline text-primary"
                        href="https://originui.com"
                        target="_blank"
                      >
                        {t("pages.login.termsOfService")}
                      </a>
                    </Label>
                  </div>
                  <Button
                    disabled={form.formState.isSubmitting}
                    onClick={form.handleSubmit(submit)}
                    className="w-full mt-4"
                    type="submit"
                  >
                    {form.formState.isSubmitting && (
                      <LoaderCircle
                        className="-ms-1 me-2 animate-spin"
                        size={16}
                      />
                    )}
                    {t("pages.login.loginButton")}
                  </Button>
                </form>
              </div>
            </CardContent>

            <CardFooter className="flex pb-6 flex-col items-center justify-center space-y-2">
              <p className="text-sm text-muted-foreground">
                {t("pages.login.termsConditions")}{" "}
                <a className="underline text-blue-500">{t("pages.login.termsConditionsLink")}</a>
              </p>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
